import { retry, RetryOptions } from './retry';

/**
 * Parse Retry-After header value to milliseconds
 */
function parseRetryAfter(retryAfterValue: string): number {
  // Try parsing as seconds (most common for 429 responses)
  const seconds = parseInt(retryAfterValue, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000; // Convert to milliseconds
  }

  // Try parsing as HTTP date
  const date = new Date(retryAfterValue);
  if (!isNaN(date.getTime())) {
    const now = new Date();
    const delayMs = date.getTime() - now.getTime();
    return Math.max(0, delayMs); // Don't return negative delays
  }

  // If we can't parse it, return 0 (no delay)
  return 0;
}

/**
 * Extract Retry-After header from various error types
 */
function getRetryAfterDelay(error: unknown): number {
  // Check if error has headers (fetch Response object)
  if (
    typeof error === 'object' &&
    error !== null &&
    'headers' in error &&
    typeof (error as { headers?: { get?: (key: string) => string } }).headers
      ?.get === 'function'
  ) {
    const retryAfter = (
      error as { headers: { get: (key: string) => string } }
    ).headers.get('retry-after');
    if (retryAfter) {
      return parseRetryAfter(retryAfter);
    }
  }

  // Check if error has a response with headers (axios-style)
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { headers?: Record<string, string> } }).response
      ?.headers
  ) {
    const headers = (error as { response: { headers: Record<string, string> } })
      .response.headers;
    const retryAfter = headers['retry-after'] ?? headers['Retry-After'];
    if (retryAfter) {
      return parseRetryAfter(retryAfter);
    }
  }

  return 0;
}

/**
 * Retry with default options for network requests
 */
export function retryNetworkCall<T>(
  fn: () => T | Promise<T>,
  options: Partial<RetryOptions> = {},
): Promise<T> {
  return retry(fn, {
    limit: 3,
    retryDelay: 1000,
    exponentialBackoff: true,
    shouldRetry: error => {
      // Retry on network errors, timeouts, and fetch errors
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        return (
          message.includes('network') ||
          message.includes('timeout') ||
          message.includes('fetch')
        );
      }

      // Retry on specific HTTP status codes
      if (typeof error === 'object' && error !== null && 'status' in error) {
        const status = (error as { status: number }).status;
        return (
          status >= 500 || // 5xx server errors
          status === 429 || // Rate limiting
          status === 408 || // Request timeout
          status === 502 || // Bad gateway
          status === 503 || // Service unavailable
          status === 504 // Gateway timeout
        );
      }

      return true;
    },
    getRetryDelay: (error, attempt, defaultDelay) => {
      // Check if this is a 429 error with Retry-After header
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error as { status: number }).status === 429
      ) {
        const retryAfterDelay = getRetryAfterDelay(error);
        if (retryAfterDelay > 0) {
          return retryAfterDelay;
        }
      }

      // For other errors, use the default delay (with exponential backoff if enabled)
      return defaultDelay;
    },
    ...options,
  });
}

export async function fetchWithRetry(
  url: string | URL | Request,
  init?: RequestInit,
  retryOptions?: Partial<RetryOptions>,
): Promise<Response> {
  return retryNetworkCall(async () => {
    return await fetch(url, init);
  }, retryOptions);
}
